import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Checkbox, Select, TextField } from '@ui'
import { listFeatures, updateFeature } from '../api.js'

/**
 * Admin-portal Features page. Each row represents either a dev-flag
 * (category === "dev") or an org-feature (category === "org"). The server
 * computes `editable` per caller — the UI trusts that boolean rather than
 * re-deriving from the caller's groups.
 *
 * Save semantics: optimistic concurrency uses the per-profile version —
 * `devFlagsVersion` for dev categories, `orgFeaturesVersion` for org.
 */
export function FeaturesPage() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [savingKey, setSavingKey] = useState(null)
  const [drafts, setDrafts] = useState({}) // { [featureKey]: { enabled, attributes } }
  const [notice, setNotice] = useState(null)

  async function refresh() {
    setError(null)
    try {
      const res = await listFeatures()
      setData(res)
      setDrafts({})
    } catch (e) {
      setError(e.message || 'Failed to load features')
    }
  }
  useEffect(() => { refresh() }, [])

  const rows = useMemo(() => {
    if (!data) return null
    const byKey = Object.fromEntries(data.values.map((v) => [v.key, v]))
    return data.flags.map((schema) => {
      const value = byKey[schema.key] || { key: schema.key, enabled: false, attributes: {} }
      return {
        schema: { ...schema, attributes: parseAwsJson(schema.attributes) },
        value:  { ...value,  attributes: parseAwsJson(value.attributes) },
      }
    })
  }, [data])

  function draftFor(key, base) {
    return drafts[key] || { enabled: base.enabled, attributes: base.attributes }
  }
  function setDraft(key, next) {
    setDrafts((d) => ({ ...d, [key]: next }))
  }
  function isDirty(key, base) {
    const d = drafts[key]
    if (!d) return false
    if (d.enabled !== base.enabled) return true
    const a = d.attributes || {}
    const b = base.attributes || {}
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    for (const k of keys) if (a[k] !== b[k]) return true
    return false
  }

  function versionFor(category) {
    return category === 'dev' ? data.devFlagsVersion : data.orgFeaturesVersion
  }

  async function save(schema, draft) {
    if (!data) return
    setSavingKey(schema.key)
    setError(null)
    try {
      // Only send attributes declared in the schema. AppConfig auto-stamps
      // _createdAt/_updatedAt onto values; sending them back would fail the
      // backend validator.
      const safeAttrs = {}
      for (const k of Object.keys(schema.attributes || {})) {
        if (draft.attributes && draft.attributes[k] !== undefined) {
          safeAttrs[k] = draft.attributes[k]
        }
      }
      await updateFeature(schema.key, {
        enabled: draft.enabled,
        attributes: JSON.stringify(safeAttrs),
        expectedVersionNumber: versionFor(schema.category),
      })
      setNotice(`Saved "${schema.name}". New configuration is live.`)
      await refresh()
    } catch (e) {
      if (e?.code === 'FEATURE_VERSION_CONFLICT') {
        await refresh()
        setError('Another admin changed features since you loaded this page. Your draft was discarded — try again.')
      } else {
        setError(e.message || 'Failed to save feature')
      }
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <>
      <div style={pageStyles.header}>
        <div>
          <h2 style={pageStyles.title}>Features</h2>
          <p style={pageStyles.sub}>
            {data?.lastDeployment
              ? `Last deployed ${formatDate(data.lastDeployment.startedAt)}` +
                (data.lastDeployment.actor ? ` by ${data.lastDeployment.actor}` : '') +
                ` (v${data.lastDeployment.versionNumber ?? '?'}, ${data.lastDeployment.state ?? '?'})`
              : 'Toggle features for this environment. Changes deploy immediately via AppConfig.'}
          </p>
        </div>
        <Button variant="secondary" onClick={refresh} disabled={savingKey !== null}>Refresh</Button>
      </div>

      {notice && (
        <div style={pageStyles.noticeOk} role="status">{notice}</div>
      )}
      {error && (
        <div style={pageStyles.noticeErr} role="alert">{error}</div>
      )}

      <Card style={{ padding: 16 }}>
        {rows === null ? (
          <p>Loading…</p>
        ) : rows.length === 0 ? (
          <p style={{ opacity: 0.7 }}>No features defined.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Feature</th>
                <th style={th}>Enabled</th>
                <th style={th}>Attributes</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ schema, value }) => {
                const d = draftFor(schema.key, value)
                const dirty = isDirty(schema.key, value)
                const disabled = !schema.editable
                return (
                  <tr key={schema.key} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={td}>
                      <div style={styles.nameRow}>
                        <span style={{ fontWeight: 600 }}>{schema.name}</span>
                        {schema.category === 'dev' && <span style={styles.devPill}>Dev</span>}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{schema.description}</div>
                    </td>
                    <td style={td}>
                      <Checkbox
                        checked={d.enabled}
                        disabled={disabled}
                        aria-label={`Enable ${schema.name}`}
                        onChange={(e) => setDraft(schema.key, { ...d, enabled: e.target.checked })}
                      />
                    </td>
                    <td style={td}>
                      <AttributeEditor
                        schema={schema.attributes}
                        values={d.attributes}
                        disabled={disabled}
                        onChange={(attrs) => setDraft(schema.key, { ...d, attributes: attrs })}
                      />
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {schema.editable && (
                        <Button
                          size="sm"
                          onClick={() => save(schema, d)}
                          disabled={!dirty || savingKey !== null}
                        >
                          {savingKey === schema.key ? 'Saving…' : 'Save'}
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}

function parseAwsJson(v) {
  if (v == null) return {}
  if (typeof v === 'string') { try { return JSON.parse(v) } catch { return {} } }
  return v
}

function AttributeEditor({ schema, values, onChange, disabled = false }) {
  const keys = Object.keys(schema || {})
  if (keys.length === 0) return <span style={{ opacity: 0.5, fontSize: 12 }}>—</span>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {keys.map((attrKey) => {
        const constraints = (schema[attrKey] && schema[attrKey].constraints) || {}
        const current = values[attrKey] ?? ''
        if (Array.isArray(constraints.enum)) {
          return (
            <Select
              key={attrKey}
              label={attrKey}
              value={String(current)}
              disabled={disabled}
              onChange={(e) => onChange({ ...values, [attrKey]: e.target.value })}
              options={constraints.enum.map((v) => ({ value: v, label: v }))}
            />
          )
        }
        return (
          <TextField
            key={attrKey}
            label={attrKey}
            value={String(current)}
            disabled={disabled}
            onChange={(e) => onChange({ ...values, [attrKey]: e.target.value })}
          />
        )
      })}
    </div>
  )
}

function formatDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 12, opacity: 0.7 }
const td = { padding: '12px', verticalAlign: 'top' }
const styles = {
  nameRow: { display: 'flex', alignItems: 'center', gap: 8 },
  devPill: {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--border)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
}
const pageStyles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  title: { fontSize: 20, fontWeight: 600, marginBottom: 2 },
  sub: { fontSize: 12, color: 'var(--text-secondary)' },
  noticeOk: {
    padding: '8px 12px',
    borderRadius: 6,
    background: 'var(--success-bg, #e6f4e6)',
    color: 'var(--success-text, #1f5d1f)',
    marginBottom: 12,
    fontSize: 13,
  },
  noticeErr: {
    padding: '8px 12px',
    borderRadius: 6,
    background: 'var(--danger-bg)',
    color: 'var(--danger-text)',
    marginBottom: 12,
    fontSize: 13,
  },
}
